import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Navbar from '@/components/layout/Navbar'
import {
  getPreAssessment, startPreAssessment, savePreAssessmentAnswers, submitPreAssessment, remainingMsAtFetch,
  type AssessmentAttempt, type AssessmentQuestion, type PreAssessmentStatus,
} from '@/services/assessmentService'
import applyBg from '@/pages/JobApplication/assets/apply-bg.png'

// แบบประเมินก่อนสมัครงาน — ต่อ backend จริง (2026-09-29):
// server สุ่มข้อ + สลับตัวเลือก / จับเวลาจาก expires_at / ตรวจคะแนนเอง / ไม่ส่งเฉลยมาหน้าเว็บ / ทำได้ครั้งเดียว
// คำตอบบันทึกไป server ทุกครั้งที่เลือก (รีเฟรช/เปลี่ยนเครื่องแล้วทำต่อได้) — หลังส่งแสดงคะแนนรวม + แยกส่วน

type ScreenState = 'loading' | 'inactive' | 'intro' | 'test' | 'review' | 'submitted'

function formatClock(totalSeconds: number): string {
  const s = Math.max(0, totalSeconds)
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

export default function PreEmploymentAssessment() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // returnTo — เพจที่พามาที่นี่ (เช่น /jobs/11/apply ตอนถูก gate ก่อนสมัครงาน) กลับไปที่นั่นหลังทำเสร็จ
  const returnTo = searchParams.get('returnTo') || '/jobs'

  const [status, setStatus] = useState<PreAssessmentStatus | null>(null)
  const [attempt, setAttempt] = useState<AssessmentAttempt | null>(null)
  const [answers, setAnswers] = useState<Record<number, number>>({})   // question_id -> ตำแหน่งตัวเลือกที่แสดง
  const [screen, setScreen] = useState<ScreenState>('loading')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [agreed, setAgreed] = useState(false)
  // แผงข้อทั้งหมดด้านซ้าย — เปิดไว้เป็นค่าเริ่มต้น พับเก็บได้
  const [navigatorOpen, setNavigatorOpen] = useState(true)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // เวลาสิ้นสุดตามนาฬิกาเครื่อง = Date.now() ตอนได้ข้อมูล + เวลาที่เหลือตาม server (ไม่พึ่งเวลาเครื่องโดยตรง)
  const [deadline, setDeadline] = useState<number | null>(null)
  const [now, setNow] = useState(() => Date.now())
  // เวลาต่อข้อ (12 วิ) — แยกจากเวลารวมทั้งชุดด้านบน รีเซ็ตใหม่ทุกครั้งที่เปลี่ยนข้อ หมดเวลาข้อไหนข้ามไปข้อถัดไปทันที
  // ไม่มีปุ่มย้อนกลับ/กระโดดข้อในระบบนี้แล้ว (ตอบเรียงลำดับทีละข้อทางเดียว) จึง "ข้าม" ที่นี่คือไปข้อถัดไปเท่านั้น
  const QUESTION_SECONDS = 12
  const [questionDeadline, setQuestionDeadline] = useState<number | null>(null)

  // รับ attempt จาก server แล้วเลือกหน้าจอ — ส่งแล้วไปหน้าผล / กำลังทำไปหน้าข้อสอบ
  const applyAttempt = useCallback((a: AssessmentAttempt) => {
    setAttempt(a)
    if (a.status === 'SUBMITTED') {
      setScreen('submitted')
      return
    }
    const picked: Record<number, number> = {}
    ;(a.questions ?? []).forEach((q) => { if (q.selected != null) picked[q.question_id] = q.selected })
    setAnswers(picked)
    setDeadline(Date.now() + remainingMsAtFetch(a))
    setNow(Date.now())
    setScreen((s) => (s === 'review' ? 'review' : 'test'))
  }, [])

  // โหลดสถานะ — เคยส่งแล้วไปหน้าผลทันที / ทำค้างไว้ทำต่อ / ยังไม่เปิดใช้แสดงข้อความ
  useEffect(() => {
    getPreAssessment()
      .then((s) => {
        setStatus(s)
        if (s.attempt) applyAttempt(s.attempt)
        else setScreen(s.is_active ? 'intro' : 'inactive')
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'โหลดแบบประเมินไม่สำเร็จ')
        setScreen('inactive')
      })
  }, [applyAttempt])

  // ส่งคำตอบ (กดส่งเอง หรือหมดเวลา) — server ตรวจคะแนนแล้วคืนผล
  const submitRef = useRef(false)
  const doSubmit = useCallback(() => {
    if (submitRef.current) return
    submitRef.current = true
    setBusy(true)
    setError('')
    submitPreAssessment(answers)
      .then(applyAttempt)
      .catch((err) => {
        submitRef.current = false
        setError(err instanceof Error ? err.message : 'ส่งแบบประเมินไม่สำเร็จ')
      })
      .finally(() => setBusy(false))
  }, [answers, applyAttempt])

  // นาฬิกานับถอยหลัง — เดินเฉพาะตอนอยู่หน้าทำข้อสอบ/ตรวจคำตอบ
  useEffect(() => {
    if (screen !== 'test' && screen !== 'review') return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [screen])

  const secondsLeft = deadline ? Math.max(0, Math.round((deadline - now) / 1000)) : 0

  // หมดเวลา → ส่งอัตโนมัติ (server ก็ปิดให้เองถ้าเราส่งไม่ทัน)
  useEffect(() => {
    if ((screen === 'test' || screen === 'review') && deadline && secondsLeft <= 0) {
      Promise.resolve().then(doSubmit)
    }
  }, [secondsLeft, screen, deadline, doSubmit])

  // กันเลื่อนข้อซ้ำ: จำ deadline ที่เพิ่งสั่งเลื่อนข้อไปแล้ว — ตอน setCurrentIndex ทำให้ re-render ก่อนที่ effect
  // รีเซ็ต questionDeadline ด้านล่างจะทันรัน เอฟเฟกต์ตรวจหมดเวลาจะยังเห็น questionDeadline ค่าเดิม(หมดเวลาแล้ว)
  // อยู่ดี ถ้าไม่กันไว้จะสั่งเลื่อนข้อซ้ำอีกรอบทันที กลายเป็นข้ามทีละ 2 ข้อ
  const advancedForDeadlineRef = useRef<number | null>(null)

  // รีเซ็ตนาฬิกา 12 วิ ทุกครั้งที่เปลี่ยนไปข้อใหม่ (เข้าหน้าทำข้อสอบ)
  useEffect(() => {
    if (screen !== 'test') { setQuestionDeadline(null); return }
    advancedForDeadlineRef.current = null
    setQuestionDeadline(Date.now() + QUESTION_SECONDS * 1000)
  }, [screen, currentIndex])

  const questionSecondsLeft = questionDeadline ? Math.max(0, Math.ceil((questionDeadline - now) / 1000)) : 0

  // หมดเวลาข้อนี้ (12 วิ) → ไปข้อถัดไปทันที ไม่ต้องกดเอง — ถ้าเป็นข้อสุดท้ายไปหน้าตรวจสอบก่อนส่ง
  useEffect(() => {
    if (screen !== 'test' || !questionDeadline || questionSecondsLeft > 0) return
    if (advancedForDeadlineRef.current === questionDeadline) return   // เลื่อนไปแล้วสำหรับ deadline นี้ ไม่ทำซ้ำ
    advancedForDeadlineRef.current = questionDeadline
    const total = attempt?.questions?.length ?? 0
    Promise.resolve().then(() => {
      if (currentIndex >= total - 1) setScreen('review')
      else setCurrentIndex((i) => i + 1)
    })
  }, [questionSecondsLeft, screen, questionDeadline, currentIndex, attempt])

  const startAssessment = () => {
    setBusy(true)
    setError('')
    startPreAssessment()
      .then((a) => { setCurrentIndex(0); applyAttempt(a) })
      .catch((err) => setError(err instanceof Error ? err.message : 'เริ่มแบบประเมินไม่สำเร็จ'))
      .finally(() => setBusy(false))
  }

  // เลือกคำตอบ → บันทึกไป server ทันที (ถ้าบันทึกไม่ผ่านจะแจ้ง แต่คำตอบบนหน้าจอยังอยู่ และส่งไปอีกครั้งตอนกดส่ง)
  const selectAnswer = (qid: number, displayIndex: number) => {
    setAnswers((prev) => ({ ...prev, [qid]: displayIndex }))
    savePreAssessmentAnswers({ [qid]: displayIndex })
      .then(() => setError(''))
      .catch((err) => setError(err instanceof Error ? err.message : 'บันทึกคำตอบไม่สำเร็จ'))
  }

  const questions: AssessmentQuestion[] = attempt?.questions ?? []
  const answeredCount = questions.filter((q) => answers[q.question_id] !== undefined).length
  const totalCount = questions.length
  const title = status?.title || 'แบบประเมินก่อนสมัครงาน'

  if (screen === 'loading') {
    return <div className="flex min-h-screen items-center justify-center bg-[#F8F7FC] text-sm text-text-muted">กำลังโหลด...</div>
  }

  // ── 0. ยังไม่เปิดใช้งาน ────────────────────────────────────────────────────
  if (screen === 'inactive') {
    return (
      <div className="min-h-screen bg-[#F8F7FC]">
        <Navbar />
        <div className="mx-auto max-w-[520px] px-4 py-32 text-center sm:px-6">
          <h1 className="text-2xl font-bold text-text">{error ? 'โหลดแบบประเมินไม่สำเร็จ' : 'ยังไม่เปิดใช้แบบประเมิน'}</h1>
          <p className="mt-3 text-sm leading-relaxed text-text-muted">
            {error || 'ขณะนี้ยังไม่ต้องทำแบบประเมินก่อนสมัครงาน คุณสามารถสมัครงานได้เลย'}
          </p>
          <button
            onClick={() => navigate(returnTo)}
            className="mt-10 flex h-12 w-full items-center justify-center bg-accent text-sm font-semibold text-white transition-colors hover:bg-accent2"
          >
            ดำเนินการสมัครงานต่อ →
          </button>
        </div>
      </div>
    )
  }

  // ── 1. INTRODUCTION ──────────────────────────────────────────────────────
  // หน้านี้ต้องไม่แสดงข้อมูลตำแหน่งงาน/บริษัทใดๆ เด็ดขาด — เป็นแบบประเมินกลางของทุกใบสมัคร
  if (screen === 'intro') {
    const minutes = status?.time_limit_minutes ?? 0
    const stats = [
      { value: `${status?.total_questions ?? 0} ข้อ`, label: 'จำนวนคำถาม' },
      { value: `${minutes} นาที`, label: 'เวลาทำทั้งหมด' },
      { value: '12 วิ/ข้อ', label: 'เวลาตอบต่อข้อ' },
    ]
    const rules = [
      'ทำแบบประเมินด้วยความรู้ของตนเอง โดยไม่ใช้ความช่วยเหลือจากผู้อื่น',
      'แต่ละข้อมีเวลาตอบ 12 วินาที นับถอยหลังทันทีที่ข้อนั้นแสดงขึ้น หมดเวลาจะข้ามไปข้อถัดไปให้อัตโนมัติ',
      'ตอบเรียงลำดับทีละข้อเท่านั้น ไม่มีปุ่มย้อนกลับ — ข้อที่ข้ามหรือตอบไปแล้วจะแก้ไขอีกไม่ได้',
      'ระบบแสดงคะแนนทันทีหลังส่ง และผลการประเมินใช้ประกอบการพิจารณาใบสมัครทุกตำแหน่ง',
    ]
    const beforeStart = [
      { title: '⏱ ทุกข้อมีเวลาตอบ 12 วินาที', body: 'พอเห็นคำถามปุ๊บนับถอยหลังทันที ครบ 12 วิจะข้ามไปข้อถัดไปให้เองโดยอัตโนมัติ ไม่ว่าจะตอบทันหรือไม่' },
      { title: 'ตอบเรียงลำดับ ย้อนกลับไม่ได้', body: 'ไม่มีปุ่มย้อนกลับหรือข้ามไปดูข้อถัดไปก่อน ต้องตอบ (หรือปล่อยให้หมดเวลา) ทีละข้อตามลำดับเท่านั้น' },
      { title: 'คำถามและตัวเลือกถูกสุ่ม', body: 'ระบบสุ่มคำถามจากคลังข้อสอบ และสุ่มลำดับตัวเลือกคำตอบของแต่ละข้อ' },
      { title: 'เวลาเริ่มนับทันที', body: `เมื่อกดเริ่ม นาฬิกา ${minutes} นาทีจะเริ่มเดินทันทีและหยุดไม่ได้` },
      { title: 'ส่งอัตโนมัติเมื่อหมดเวลา', body: 'หากหมดเวลา ระบบจะส่งคำตอบที่บันทึกไว้ให้โดยอัตโนมัติ' },
      { title: 'ทำซ้ำไม่ได้', body: 'เมื่อส่งแล้วจะไม่สามารถแก้ไขคำตอบหรือทำแบบประเมินใหม่ได้อีก' },
    ]

    return (
      <div className="relative isolate min-h-screen overflow-hidden bg-[#FAFAFF]">
        {/* พื้นหลัง logistics จางๆ ชุดเดียวกับหน้าสมัครงาน + ทับขาวบางๆ ให้เนื้อหาเด่น */}
        <img src={applyBg} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover" />
        <div className="pointer-events-none absolute inset-0 -z-10 bg-white/40" aria-hidden="true" />

        <Navbar />

        {/* Navbar เป็น fixed — เว้นที่ด้านบนให้พ้น */}
        <div className="mx-auto grid max-w-[1160px] grid-cols-1 gap-12 px-4 pb-20 pt-32 sm:px-6 lg:grid-cols-[1.15fr_1fr] lg:gap-20 lg:pt-40">

          {/* ── ซ้าย: หัวข้อ + สถิติ + กติกา ── */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#6C63FF]">Pre-Assessment</p>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.3] text-text sm:text-[46px]">
              {title}
            </h1>
            {status?.sections && status.sections.length > 0 && (
              <p className="mt-2 text-lg text-text-muted sm:text-xl">
                {status.sections.map((s) => `${s.title} (${s.pick_count} ข้อ)`).join(' · ')}
              </p>
            )}
            <p className="mt-6 max-w-[520px] text-[15px] leading-[1.9] text-text-muted">
              แบบประเมินกลางสำหรับผู้สมัครงานทุกคน ทำเพียง 1 ครั้ง และผลการประเมินจะใช้ประกอบการสมัครงานทุกตำแหน่งบน JosCoWork
            </p>

            <div className="mt-10 grid max-w-[520px] grid-cols-3 border-y border-[#E7E5F4] sm:mt-12">
              {stats.map((s, i) => (
                // มือถือ: จัดกลางในแต่ละช่องให้สมดุล + ตัวเลขเล็กลงและไม่ตัดบรรทัด / จอใหญ่: ชิดซ้ายเหมือนเดิม
                <div key={s.label} className={`px-1 py-6 text-center sm:px-0 sm:text-left ${i > 0 ? 'border-l border-[#E7E5F4] sm:pl-5' : ''}`}>
                  <p className="whitespace-nowrap text-lg font-semibold text-text sm:text-2xl">{s.value}</p>
                  <p className="mt-1.5 text-[11px] leading-snug text-text-muted sm:text-xs">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 max-w-[520px]">
              <p className="text-sm font-semibold text-text">กติกาการทำแบบประเมิน</p>
              <ol className="mt-5 space-y-4">
                {rules.map((rule, i) => (
                  <li key={rule} className="flex gap-4 text-[15px] leading-relaxed text-text-muted">
                    <span className="w-6 shrink-0 pt-px text-sm font-semibold tabular-nums text-[#6C63FF]">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {rule}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* ── ขวา: การ์ดก่อนเริ่ม + ยืนยัน + ปุ่มเริ่ม ── */}
          <div className="lg:pt-2">
            <div className="border border-[#E7E5F4] border-t-2 border-t-[#6C63FF] bg-white/95 p-7 shadow-[0_24px_60px_-32px_rgba(108,99,255,0.35)] backdrop-blur sm:p-9 lg:sticky lg:top-32">
              <h2 className="text-lg font-semibold text-text">ก่อนเริ่มทำแบบประเมิน</h2>

              <div className="mt-6 divide-y divide-[#EFEDF8]">
                {beforeStart.map((item) => (
                  <div key={item.title} className="py-4 first:pt-0">
                    <p className="text-sm font-semibold text-text">{item.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-text-muted">{item.body}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 border-t border-[#EFEDF8] pt-6">
                <label className="flex cursor-pointer items-start gap-3 bg-[#F5F3FF] px-4 py-3.5 text-sm leading-relaxed text-text">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-[#6C63FF]"
                  />
                  ฉันได้อ่านและเข้าใจเงื่อนไขของแบบประเมินแล้ว รวมถึงเข้าใจว่าแต่ละข้อมีเวลาตอบ 12 วินาที และไม่สามารถย้อนกลับไปแก้ไขคำตอบได้
                </label>

                {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}

                <button
                  onClick={startAssessment}
                  disabled={!agreed || busy}
                  className="mt-4 flex h-12 w-full items-center justify-center bg-[#6C63FF] text-[15px] font-semibold text-white transition-colors hover:bg-[#5A51E8] disabled:cursor-not-allowed disabled:bg-[#DCDAEC]"
                >
                  {busy ? 'กำลังเตรียมข้อสอบ...' : 'เริ่มทำแบบประเมิน →'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ── 4. SUBMITTED — แสดงคะแนนรวม + แยกส่วน (ไม่แสดงเฉลยรายข้อ) ─────────────
  if (screen === 'submitted' && attempt) {
    const score = attempt.score ?? 0
    const total = attempt.question_count
    const pct = total > 0 ? Math.round((score / total) * 100) : 0
    const submittedDate = attempt.submitted_at
      ? new Date(attempt.submitted_at).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '-'

    return (
      <div className="min-h-screen bg-[#F8F7FC]">
        <Navbar />
        <div className="mx-auto max-w-[520px] px-4 py-24 text-center sm:px-6">
          <h1 className="text-2xl font-bold text-text">ส่งแบบประเมินเรียบร้อยแล้ว</h1>
          <p className="mt-3 text-sm leading-relaxed text-text-muted">
            ผลการประเมินจะถูกใช้ประกอบการพิจารณาใบสมัครทุกตำแหน่ง
          </p>

          <div className="mt-10 border border-[#E7E5F4] border-t-2 border-t-[#6C63FF] bg-white px-6 py-8">
            <p className="text-sm text-text-muted">คะแนนของคุณ</p>
            <p className="mt-2 text-5xl font-bold tabular-nums text-text">
              {score}<span className="text-2xl font-semibold text-text-muted"> / {total}</span>
            </p>
            <p className="mt-2 text-sm font-medium text-[#6C63FF]">{pct}%</p>

            {attempt.section_scores && attempt.section_scores.length > 0 && (
              <div className="mt-8 space-y-4 border-t border-[#EFEDF8] pt-6 text-left">
                {attempt.section_scores.map((s) => {
                  const sp = s.total > 0 ? (s.score / s.total) * 100 : 0
                  return (
                    <div key={s.code}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text">{s.title}</span>
                        <span className="font-semibold tabular-nums text-text">{s.score} / {s.total}</span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[#ECEAF7]">
                        <div className="h-full rounded-full bg-[#6C63FF]" style={{ width: `${sp}%` }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between text-sm">
            <span className="text-text-muted">วันที่ส่ง</span>
            <span className="font-medium text-text">{submittedDate}</span>
          </div>

          <button
            onClick={() => navigate(returnTo)}
            className="mt-10 flex h-12 w-full items-center justify-center gap-2 bg-accent text-sm font-semibold text-white transition-colors hover:bg-accent2"
          >
            ดำเนินการสมัครงานต่อ →
          </button>
        </div>
      </div>
    )
  }

  if (!attempt || questions.length === 0) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F8F7FC] text-sm text-text-muted">กำลังโหลด...</div>
  }

  const timerTone = secondsLeft <= 60 ? 'strong' : secondsLeft <= 300 ? 'warn' : 'normal'
  const timerCls = timerTone === 'strong' ? 'text-rose-600' : timerTone === 'warn' ? 'text-amber-600' : 'text-text'

  // ── 2. TEST ────────────────────────────────────────────────────────────────
  if (screen === 'test') {
    const q = questions[currentIndex]
    const isLast = currentIndex === questions.length - 1

    const progressPct = totalCount > 0 ? (answeredCount / totalCount) * 100 : 0
    const timerPillCls =
      timerTone === 'strong' ? 'bg-rose-50 text-rose-600'
        : timerTone === 'warn' ? 'bg-amber-50 text-amber-700'
          : 'bg-[#F1EFFF] text-[#4B43C9]'

    return (
      <div className="min-h-screen bg-[#FAFAFD]">
        {/* ── sticky header: ชื่อ + ข้อปัจจุบัน / progress bar / เวลาคงเหลือ ── */}
        <header className="sticky top-0 z-20 border-b border-[#ECEAF5] bg-white">
          <div className="mx-auto grid max-w-[1180px] grid-cols-[1fr_auto] items-center gap-4 px-4 py-4 sm:px-6 md:grid-cols-[1fr_minmax(0,320px)_1fr]">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-text">{title}</p>
              <p className="mt-0.5 text-xs tabular-nums text-text-muted">
                ข้อ {String(currentIndex + 1).padStart(2, '0')} / {totalCount}
              </p>
            </div>

            <div className="order-last col-span-2 md:order-none md:col-span-1">
              <div
                className="h-1 w-full overflow-hidden rounded-full bg-[#ECEAF7]"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={totalCount}
                aria-valuenow={answeredCount}
                aria-label="ความคืบหน้าแบบประเมิน"
              >
                <div className="h-full rounded-full bg-[#6C63FF] transition-[width] duration-300" style={{ width: `${progressPct}%` }} />
              </div>
              <p className="mt-1.5 text-center text-[11px] tabular-nums text-text-muted">
                ตอบแล้ว {answeredCount} จาก {totalCount} ข้อ
              </p>
            </div>

            <div className="flex justify-end">
              <p className={`rounded-full px-4 py-1.5 text-sm font-semibold tabular-nums ${timerPillCls}`}>
                เหลือเวลา {formatClock(secondsLeft)}
              </p>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-[1180px] px-4 pb-20 pt-10 sm:px-6 lg:pt-14">
          <div className={`grid grid-cols-1 gap-10 ${navigatorOpen ? 'lg:grid-cols-[280px_1fr] lg:gap-16' : 'lg:grid-cols-[auto_1fr] lg:gap-12'}`}>

            {/* ── ซ้าย: แผงข้อทั้งหมด ── */}
            {/* มือถือ: แผงข้อทั้งหมดไว้ล่างสุด (order-2) ให้เห็นโจทย์ก่อน / จอใหญ่: อยู่ซ้ายเหมือนเดิม */}
            <aside className="order-2 lg:order-1 lg:sticky lg:top-28 lg:self-start">
              <QuestionNavigator
                questions={questions}
                answers={answers}
                currentIndex={currentIndex}
                open={navigatorOpen}
                onToggle={() => setNavigatorOpen((o) => !o)}
              />
            </aside>

            {/* ── ขวา: คำถาม + ตัวเลือก + ปุ่มนำทาง ── */}
            <main className="order-1 min-w-0 max-w-[760px] lg:order-2">
              {/* ป้ายเหนือโจทย์แสดงชื่อส่วนที่ข้อนี้มาจาก + นาฬิกา 12 วิของข้อนี้ */}
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs font-medium tracking-wide text-[#6C63FF]">
                  {q.section_title} · ข้อ {currentIndex + 1}
                </p>
                <p
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold tabular-nums ${
                    questionSecondsLeft <= 4 ? 'bg-rose-50 text-rose-600' : 'bg-[#F1EFFF] text-[#4B43C9]'
                  }`}
                >
                  หมดเวลาข้อนี้ใน {questionSecondsLeft} วิ
                </p>
              </div>
              <h1 className="mt-3 text-[22px] font-semibold leading-[1.6] text-text sm:text-[26px]">{q.question_text}</h1>

              <div className="mt-9 space-y-3" role="radiogroup" aria-label="ตัวเลือกคำตอบ">
                {q.options.map((text, displayIdx) => {
                  const active = answers[q.question_id] === displayIdx
                  return (
                    <button
                      key={displayIdx}
                      role="radio"
                      aria-checked={active}
                      onClick={() => selectAnswer(q.question_id, displayIdx)}
                      className={`flex w-full items-center gap-4 rounded-xl border px-5 py-[18px] text-left transition-colors ${
                        active
                          ? 'border-[#6C63FF] bg-[#F7F6FF]'
                          : 'border-[#E6E4F0] bg-white hover:bg-[#FAF9FF]'
                      }`}
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold ${
                          active ? 'bg-[#6C63FF] text-white' : 'bg-[#F3F2F8] text-text-muted'
                        }`}
                      >
                        {String.fromCharCode(65 + displayIdx)}
                      </span>
                      <span className="flex-1 text-[15px] leading-relaxed text-text">{text}</span>
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                          active ? 'border-[#6C63FF]' : 'border-[#D6D3E6]'
                        }`}
                        aria-hidden="true"
                      >
                        {active && <span className="h-2.5 w-2.5 rounded-full bg-[#6C63FF]" />}
                      </span>
                    </button>
                  )
                })}
              </div>

              {error && <p className="mt-6 text-sm text-rose-600">{error}</p>}

              {/* ไม่มีปุ่มย้อนกลับ — ตอบเรียงลำดับทีละข้อทางเดียวเท่านั้น (ดูกติกาหน้าเริ่มต้น) */}
              <div className="mt-12 flex flex-wrap items-center justify-end gap-4 border-t border-[#ECEAF5] pt-6">
                <div className="flex items-center gap-5">
                  {isLast ? (
                    <button
                      onClick={() => setScreen('review')}
                      className="flex h-11 items-center rounded-lg bg-[#6C63FF] px-6 text-sm font-semibold text-white transition-colors hover:bg-[#5A51E8]"
                    >
                      ตรวจสอบและส่งคำตอบ →
                    </button>
                  ) : (
                    <button
                      onClick={() => setCurrentIndex((i) => Math.min(questions.length - 1, i + 1))}
                      className="flex h-11 items-center rounded-lg bg-[#6C63FF] px-7 text-sm font-semibold text-white transition-colors hover:bg-[#5A51E8]"
                    >
                      ถัดไป →
                    </button>
                  )}
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    )
  }

  // ── 3. REVIEW ANSWERS ────────────────────────────────────────────────────
  const unanswered = questions.filter((q) => answers[q.question_id] === undefined)
  return (
    <div className="min-h-screen bg-[#F8F7FC]">
      <Navbar />
      {/* Navbar เป็น fixed (สูง 64px) — เว้นด้านบนให้พ้น ไม่งั้นหัวข้อถูกบังครึ่งหนึ่ง */}
      <div className="mx-auto max-w-[640px] px-4 pb-14 pt-28 sm:px-6">
        <h1 className="text-2xl font-bold text-text">ตรวจสอบก่อนส่งแบบประเมิน</h1>

        <div className="mt-8 grid grid-cols-3 gap-6 border-t border-border pt-8">
          <div>
            <p className="text-xs text-text-muted">ตอบแล้ว</p>
            <p className="mt-1 text-xl font-semibold text-text">{answeredCount} / {totalCount} ข้อ</p>
          </div>
          <div>
            <p className="text-xs text-text-muted">ยังไม่ได้ตอบ</p>
            <p className={`mt-1 text-xl font-semibold ${unanswered.length > 0 ? 'text-rose-600' : 'text-text'}`}>{unanswered.length} ข้อ</p>
          </div>
          <div>
            <p className="text-xs text-text-muted">เวลาคงเหลือ</p>
            <p className={`mt-1 text-xl font-semibold tabular-nums ${timerCls}`}>{formatClock(secondsLeft)}</p>
          </div>
        </div>

        <div className="mt-8 border-t border-border pt-8">
          <p className="mb-3 text-sm font-medium text-text">ภาพรวมคำตอบ</p>
          <div className="grid grid-cols-8 gap-2 sm:grid-cols-10">
            {questions.map((q, i) => {
              const isAnswered = answers[q.question_id] !== undefined
              return (
                <div
                  key={q.question_id}
                  className={`flex h-9 w-9 items-center justify-center border text-xs font-medium ${
                    isAnswered ? 'border-transparent bg-[#EDEBFA] text-text' : 'border-rose-300 bg-rose-50 text-rose-600'
                  }`}
                >
                  {i + 1}
                </div>
              )
            })}
          </div>
        </div>

        {error && <p className="mt-6 text-sm text-rose-600">{error}</p>}

        {/* ไม่มีปุ่มย้อนกลับไปหน้าทำข้อสอบแล้ว — กลับไปจะรีเซ็ตนาฬิกา 12 วิของข้อสุดท้ายใหม่ได้ ขัดกับกติกาตอบทางเดียว */}
        <div className="mt-10 flex items-center justify-end border-t border-border pt-8">
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            className="flex h-11 items-center gap-2 bg-accent px-6 text-sm font-semibold text-white transition-colors hover:bg-accent2 disabled:opacity-50"
          >
            {busy ? 'กำลังส่ง...' : 'ส่งแบบประเมิน'}
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 px-4">
          <div className="w-full max-w-sm bg-white p-7">
            <p className="text-base font-semibold text-text">ยืนยันการส่งแบบประเมิน?</p>
            <p className="mt-2 text-sm leading-relaxed text-text-muted">
              เมื่อส่งแล้วจะไม่สามารถแก้ไขคำตอบหรือทำแบบประเมินนี้ใหม่ได้
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setConfirmOpen(false)} className="text-sm font-medium text-text-muted hover:text-text">
                ยกเลิก
              </button>
              <button
                onClick={() => { setConfirmOpen(false); doSubmit() }}
                className="flex h-10 items-center bg-accent px-5 text-sm font-semibold text-white transition-colors hover:bg-accent2"
              >
                ยืนยันและส่ง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Question Navigator — แผงข้อทั้งหมดด้านซ้าย พับเก็บได้ ────────────────────────
// แสดงความคืบหน้าอย่างเดียว กดข้ามไปข้อไหนไม่ได้แล้ว (ตอบเรียงลำดับทีละข้อทางเดียวตามกติกาใหม่)
function QuestionNavigator({
  questions, answers, currentIndex, open, onToggle,
}: {
  questions: AssessmentQuestion[]
  answers: Record<number, number>
  currentIndex: number
  open: boolean
  onToggle: () => void
}) {
  if (!open) {
    return (
      <button
        onClick={onToggle}
        aria-expanded={false}
        className="text-xs font-medium text-text-muted transition-colors hover:text-[#6C63FF] lg:[writing-mode:vertical-rl]"
      >
        แสดงข้อทั้งหมด
      </button>
    )
  }

  return (
    <div className="rounded-xl border border-[#ECEAF5] bg-white p-5">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-text">ข้อทั้งหมด ({questions.length} ข้อ)</p>
        <button
          onClick={onToggle}
          aria-expanded={true}
          className="text-xs text-text-muted transition-colors hover:text-[#6C63FF]"
        >
          ซ่อน
        </button>
      </div>

      <div className="mt-5 grid grid-cols-8 gap-2 sm:grid-cols-10 lg:grid-cols-6">
        {questions.map((q, i) => {
          const isAnswered = answers[q.question_id] !== undefined
          const isCurrent = i === currentIndex
          return (
            <div
              key={q.question_id}
              aria-current={isCurrent ? 'step' : undefined}
              aria-label={`ข้อ ${i + 1}${isAnswered ? ' (ตอบแล้ว)' : ''}`}
              className={`flex aspect-square w-full max-w-9 items-center justify-center rounded-full text-xs font-medium tabular-nums ${
                isCurrent
                  ? 'bg-[#6C63FF] text-white'
                  : isAnswered
                    ? 'bg-[#ECEAFF] text-[#4B43C9]'
                    : 'border border-[#E3E1EE] bg-white text-text-muted'
              }`}
            >
              {i + 1}
            </div>
          )
        })}
      </div>

      <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 border-t border-[#F0EEF7] pt-4 text-[11px] text-text-muted">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#6C63FF]" /> ข้อปัจจุบัน</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#ECEAFF]" /> ตอบแล้ว</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border border-[#D6D3E6]" /> ยังไม่ได้ตอบ</span>
      </div>
    </div>
  )
}
